import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import GalleryTab from '@/components/admin/GalleryTab';
import ServicesTab from '@/components/admin/ServicesTab';
import SettingsTab from '@/components/admin/SettingsTab';
import CategoriesTab from '@/components/admin/CategoriesTab';
import ReviewsTab from '@/components/admin/ReviewsTab';

import PackagesTab from '@/components/admin/PackagesTab';
import FormFieldsTab from '@/components/admin/FormFieldsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_ADJ = { opacity: 70, brightness: 0, contrast: 0 };
const DEFAULT_SOCIAL = { instagram: '', tiktok: '', email: '' };

const TABS = [
  { id: 'gallery', label: 'Gallery' },
  { id: 'services', label: 'Services' },
  { id: 'packages', label: 'Packages' },
  { id: 'form', label: 'Form Fields' },
  { id: 'settings', label: 'Settings' },
  { id: 'categories', label: 'Categories' },
  { id: 'reviews', label: 'Reviews' },
];

const loginReveal = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('gallery');

  const [photos, setPhotos] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [packages, setPackages] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [settings, setSettings] = useState({
    logoUrl: '',
    heroImage: '',
    socialLinks: DEFAULT_SOCIAL,
    aboutText: '',
    heroAdjustments: DEFAULT_HERO_ADJ,
  });

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data);
    } catch { toast.error('Failed to load photos'); }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch { toast.error('Failed to load services'); }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Categories:', err); }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Reviews:', err); }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Packages:', err); }
  }, []);

  const fetchFormFields = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/form-fields`);
      setFormFields(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('FormFields:', err); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings({
        logoUrl: res.data.logo_url || '',
        heroImage: res.data.hero_image || '',
        socialLinks: res.data.social_links || DEFAULT_SOCIAL,
        aboutText: res.data.about_text || '',
        heroAdjustments: res.data.hero_adjustments || DEFAULT_HERO_ADJ,
      });
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Settings:', err); }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchPhotos();
    fetchServices();
    fetchCategories();
    fetchReviews();
    fetchPackages();
    fetchFormFields();
    fetchSettings();
  }, [isAuthenticated, fetchPhotos, fetchServices, fetchCategories, fetchReviews, fetchPackages, fetchFormFields, fetchSettings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setIsAuthenticated(true);
        toast.success('Login successful');
      }
    } catch {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <Toaster position="top-right" theme="dark" />
        <motion.div className="w-full max-w-md" {...loginReveal}>
          <h1 className="text-4xl md:text-6xl font-normal tracking-tight mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors" placeholder="Enter admin password" required data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50" data-testid="login-button">
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <Toaster position="top-right" theme="dark" />
      <motion.div {...loginReveal}>
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-4xl md:text-6xl font-normal tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Dashboard</h1>
          <button onClick={() => setIsAuthenticated(false)} className="bg-transparent border border-white/30 text-white hover:bg-white hover:text-black rounded-none px-6 py-2 text-sm uppercase tracking-widest transition-all duration-300" data-testid="logout-button">Logout</button>
        </div>
        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`shrink-0 whitespace-nowrap pb-4 px-4 md:px-6 text-xs md:text-sm uppercase tracking-widest transition-colors ${activeTab === tab.id ? 'border-b-2 border-white text-white' : 'text-gray-400 hover:text-white'}`} data-testid={`${tab.id}-tab`}>
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'gallery' && <GalleryTab photos={photos} categories={categories} onRefresh={fetchPhotos} />}
        {activeTab === 'services' && <ServicesTab services={services} onRefresh={fetchServices} />}
        {activeTab === 'packages' && <PackagesTab packages={packages} onRefresh={fetchPackages} />}
        {activeTab === 'form' && <FormFieldsTab formFields={formFields} onRefresh={fetchFormFields} />}
        {activeTab === 'settings' && <SettingsTab settings={settings} onRefresh={fetchSettings} />}
        {activeTab === 'categories' && <CategoriesTab categories={categories} onRefresh={fetchCategories} />}
        {activeTab === 'reviews' && <ReviewsTab reviews={reviews} onRefresh={fetchReviews} />}
      </motion.div>
    </div>
  );
};

export default Admin;
